import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { Easing } from "../../transforms/transform_base.js";
import { TransformOpacity } from "../../transforms/transform_opacity.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Vfx2DManager } from "../../webgl_2d/vfx_2d_manager.js";
import { CoinCounterPill, TopCounterLayout } from "../common_ui_elements/coin_counter_pill.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { DOM_UI_KEYS, markCanvasHasContext, resolveUiBackend, resyncDomHost } from "../dom_backend/dom_backend.js";
import { createNullCanvasContext } from "../dom_backend/null_canvas_context.js";

export const COIN_FLIGHT_HUD_FADE_MS = 200;

class CoinFlightHudRoot extends UIElementHolder
{
  public name(): string { return "CoinFlightHud"; }

  public update( now: number ): void
  {
    this.updateBasic( now );
  }
}

/**
 * The coin counter shown while reward coins fly to it across a screen change
 * (win overlay → loading → next level, or → menu).
 *
 * It lives on its own layer above overlays and below the overlay-front VFX canvas
 * the coins fly on, so exactly one counter is visible for the whole flight no
 * matter which screen is underneath or how long it takes to appear.
 *
 * A screen with its own counter on the same pixels (the menu) docks it here. While
 * the flight runs that counter is hidden, even before its screen is shown — two
 * copies on top of each other double the semi-transparent shadows of the pill and
 * the coin — and when the last coin lands over it the two swap in one frame, which
 * is invisible because both come from TopCounterLayout. Over a screen without one
 * (the level) the counter fades out.
 *
 * update() runs BEFORE the screens update: the docked counter's opacity change
 * then bubbles up through the screen's own update and is painted in this frame.
 */
export class CoinFlightHud
{
  private static readonly POP_MS = 160;
  private static readonly SETTLE_MS = 100;
  private static readonly POP_FROM_SCALE = 0.6;
  private static readonly POP_OVERSHOOT = 1.12;

  private static canvas: HTMLCanvasElement | null = null;
  private static ctx: CanvasRenderingContext2D | null = null;
  private static dom_mode = false;
  private static width = 0;
  private static height = 0;
  private static dpr = 1;

  private static root: CoinFlightHudRoot | null = null;
  private static pill: CoinCounterPill | null = null;

  private static active = false;
  private static docked: CoinCounterPill | null = null;
  private static hidden_docked: CoinCounterPill | null = null;
  private static hold_until = 0;
  private static fade_end_at = 0;

  /** Creates the layer right after #overlayCanvas: same z-index, later in the DOM, so it paints above overlays. */
  public static init( left: number, width: number, height: number, dpr: number ): void
  {
    if ( this.canvas )
      return;

    const canvas = document.createElement( "canvas" );
    canvas.id = "coinFlightHudCanvas";
    const s = canvas.style;
    s.position = "fixed";
    s.top = "0px";
    s.zIndex = "123";
    s.pointerEvents = "none";
    s.display = "none";

    const overlay_canvas = document.getElementById( "overlayCanvas" );
    if ( overlay_canvas?.parentNode )
      overlay_canvas.parentNode.insertBefore( canvas, overlay_canvas.nextSibling );
    else
      document.body.appendChild( canvas );

    this.canvas = canvas;
    this.dom_mode = resolveUiBackend( DOM_UI_KEYS.OVERLAY ) === "dom";
    if ( this.dom_mode )
    {
      this.ctx = createNullCanvasContext( canvas );
    }
    else
    {
      this.ctx = canvas.getContext( "2d" );
      if ( this.ctx )
        markCanvasHasContext( canvas );
    }

    this.setScreenSize( left, width, height, dpr );
  }

  public static setScreenSize( left: number, width: number, height: number, dpr: number ): void
  {
    const canvas = this.canvas;
    if ( !canvas )
      return;

    this.width = width;
    this.height = height;
    this.dpr = dpr;
    canvas.style.left = `${left}px`;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    if ( !this.dom_mode && this.ctx )
    {
      canvas.width = Math.round( width * dpr );
      canvas.height = Math.round( height * dpr );
      this.ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );
    }

    // Layout depends on the screen size: rebuild on the next show.
    const was_active = this.active;
    this.destroyRoot();
    if ( was_active )
      this.show( performance.now(), false );
  }

  /** The shown screen's own counter at the standard spot (the menu's), or null to undock it. */
  public static dock( pill: CoinCounterPill ): void
  {
    this.docked = pill;
    if ( this.active && this.fade_end_at === 0 )
      this.hideDocked();
  }

  public static undock( pill: CoinCounterPill ): void
  {
    if ( this.docked === pill )
      this.docked = null;
    if ( this.hidden_docked === pill )
      this.restoreDocked();
  }

  /** Where flying coins land: the icon of the visible counter. */
  public static coinTarget(): Vector2
  {
    return this.pill?.iconCenterAbsolute() ?? TopCounterLayout.coinIconCenter();
  }

  public static isActive(): boolean
  {
    return this.active;
  }

  /**
   * Shows the counter until every coin flight registered with Vfx2DManager has
   * landed. `pop` plays the appear bounce (the win overlay's Claim); without it
   * the counter is just there, matching one that is already on screen.
   */
  public static show( now: number, pop: boolean ): void
  {
    if ( !this.canvas || !this.ctx )
      return;

    this.ensureRoot();
    const root = this.root!;
    const pill = this.pill!;

    // Visible before any animation starts: WAAPI started on a hidden host is
    // paused, and the pop would stick at its overshoot on a reused counter.
    this.canvas.style.display = "block";
    resyncDomHost( this.canvas );

    pill.syncCoins();
    root.stopAllTransforms();
    root.setOpacityImmediate( 1 );
    pill.stopAllTransforms();
    this.fade_end_at = 0;
    this.active = true;
    this.hold_until = now;

    if ( pop )
    {
      const settle_at = now + CoinFlightHud.POP_MS;
      pill.setOpacityImmediate( 0 );
      pill.setScaleImmediate( new Vector2( CoinFlightHud.POP_FROM_SCALE, CoinFlightHud.POP_FROM_SCALE ) );
      pill.addTransform( new TransformOpacity( pill, true, 0, 1, now, CoinFlightHud.POP_MS, { easing: Easing.EaseOutQuad } ) );
      pill.addTransform( new TransformScale( pill, true, null,
        new Vector2( CoinFlightHud.POP_OVERSHOOT, CoinFlightHud.POP_OVERSHOOT ), now, CoinFlightHud.POP_MS, { easing: Easing.EaseOutQuad } ) );
      pill.addTransform( new TransformScale( pill, true, null,
        new Vector2( 1, 1 ), settle_at, CoinFlightHud.SETTLE_MS, { easing: Easing.EaseInOutQuad } ) );
      this.hold_until = settle_at + CoinFlightHud.SETTLE_MS;
    }
    else
    {
      pill.setOpacityImmediate( 1 );
      pill.setScaleImmediate( new Vector2( 1, 1 ) );
    }

    this.hideDocked();
    root.forceMakeShouldBeRendered();
  }

  /** Drops the counter at once (a screen change that clears the coin VFX too). */
  public static hide(): void
  {
    if ( !this.active )
      return;

    this.active = false;
    this.fade_end_at = 0;
    this.restoreDocked();
    this.root?.stopAllTransforms();
    this.pill?.stopAllTransforms();
    if ( this.canvas )
      this.canvas.style.display = "none";
    if ( !this.dom_mode && this.ctx )
      this.ctx.clearRect( 0, 0, this.width, this.height );
  }

  public static update( now: number ): void
  {
    if ( !this.active || !this.root || !this.pill )
      return;

    if ( this.pill.syncCoins() )
      this.root.forceMakeShouldBeRendered();

    if ( this.fade_end_at === 0 )
    {
      this.hideDocked();

      const landed_at = Math.max( this.hold_until, Vfx2DManager.getLatestCoinFlyFinishTime() );
      if ( now >= landed_at && this.hidden_docked && this.isOnVisibleLayer( this.hidden_docked ) )
      {
        // Same pixels underneath: swap in this frame, no fade.
        this.hide();
        return;
      }
      if ( now >= landed_at )
      {
        this.restoreDocked();
        this.root.fadeOut( COIN_FLIGHT_HUD_FADE_MS, now, Easing.EaseOutQuad );
        this.fade_end_at = now + COIN_FLIGHT_HUD_FADE_MS;
      }
    }
    else if ( now >= this.fade_end_at )
    {
      this.hide();
      return;
    }

    this.root.update( now );
  }

  public static render(): void
  {
    const root = this.root;
    if ( !this.active || !root || !root.shouldBeRendered() )
      return;

    if ( this.dom_mode )
      root.mountDomHost( this.canvas! );
    else
      this.ctx!.clearRect( 0, 0, this.width, this.height );
    root.render();
  }

  /** Keeps the docked counter hidden while this one is shown. */
  private static hideDocked(): void
  {
    const docked = this.docked;
    if ( docked === this.hidden_docked )
      return;

    this.restoreDocked();
    if ( !docked )
      return;

    docked.setOpacityImmediate( 0 );
    docked.forceMakeShouldBeRendered();
    this.hidden_docked = docked;
  }

  private static isOnVisibleLayer( pill: CoinCounterPill ): boolean
  {
    return pill.getContext().canvas.style.display !== "none";
  }

  private static restoreDocked(): void
  {
    const docked = this.hidden_docked;
    if ( !docked )
      return;

    this.hidden_docked = null;
    docked.setOpacityImmediate( 1 );
    docked.forceMakeShouldBeRendered();
  }

  private static ensureRoot(): void
  {
    if ( this.root || !this.ctx )
      return;

    const ctx = this.ctx;
    const root = new CoinFlightHudRoot( ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), false, false );
    root.setClickable( false );

    const pill = new CoinCounterPill( ctx, TopCounterLayout.coinX(), TopCounterLayout.y() );
    root.addChild( pill );

    this.root = root;
    this.pill = pill;
  }

  private static destroyRoot(): void
  {
    this.root?.stopAllTransforms();
    if ( this.dom_mode )
      this.root?.getDomNode().remove();
    this.root = null;
    this.pill = null;
    this.active = false;
    this.restoreDocked();
    this.fade_end_at = 0;
    if ( this.canvas )
      this.canvas.style.display = "none";
  }
}

